import React from 'react';
import {
    Box,
    Button,
    Checkbox,
    Container,
    Dialog,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
} from '@mui/material';
import withStyles from '@mui/styles/withStyles';
import { channelTypeItems } from '../../constants/manage-device-profile-data';
import deviceStyles from '../../app/device-management/DeviceStyle';
import { ALPHA_NUMBERIC } from '../../constants';
import { DerivedChannelData } from '../../types';
import makeStyles from '@mui/styles/makeStyles';
import { useToasts } from '../../data/DataProviders/ToastProvider';

const useStyles = makeStyles(() => ({
    root: {
        width: '650px',
    },

    Container: {
        margin: '10px',
        padding: '10px',
        height: '100%',
        width: '575px',
        overflowX: 'hidden',
    },
}));
const StyledDialog = withStyles({
    root: {
        '& .MuiDialog-paper': {
            width: '650px',
            overflow: 'auto',
            height: '75%',
        },
    },
})(Dialog);

export const DerivedChannelModal: React.FC<{
    open: boolean;
    onClose: Function;
    derivedChannels: DerivedChannelData[];
    derivedChannelData: Function;
}> = (props) => {
    const { onClose, open, derivedChannels } = props;
    const classes = deviceStyles();
    const rootClass = useStyles();
    const { addToast } = useToasts();
    const [derivedChannelData, setDerivedChannelData] = React.useState<DerivedChannelData>({
        name: '',
        dataType: '',
        unit: '',
        displayUnit: '',
        equation: '',
        sync: false,
        checked: true,
    });

    const reset = (): void => {
        derivedChannelData.name = '';
        derivedChannelData.dataType = '';
        derivedChannelData.displayUnit = '';
        derivedChannelData.unit = '';
        derivedChannelData.sync = false;
        derivedChannelData.equation = '';
    };


    const handleCheckChange = (value: boolean, type: string): void => {
        if (derivedChannelData && value !== null) {
            const channelDataObj: any = { ...derivedChannelData };
            channelDataObj[`${type}`] = Boolean(value);
            setDerivedChannelData(channelDataObj);
        }
    };

    const isDisable = (): boolean | undefined => {
        if (
            derivedChannelData.name !== '' &&
            derivedChannelData.dataType !== '' &&
            derivedChannelData.displayUnit !== '' &&
            derivedChannelData.unit !== ''
        )
            return false;
        return true;
    };
    const onChangeValues = (value: string, type: string): void => {
        if (derivedChannelData) {
            const channelDataObj: any = { ...derivedChannelData };
            channelDataObj[`${type}`] = value;
            setDerivedChannelData(channelDataObj);
        }
    };

    const onChangeChannelName = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): void => {
        if (e.target.value === '' || ALPHA_NUMBERIC.test(e.target.value)) {
            onChangeValues(e.target.value, 'name');
        }
    };

    const onChangeUnit = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): void => {
        if (e.target.value === '' || ALPHA_NUMBERIC.test(e.target.value)) {
            onChangeValues(e.target.value, 'unit');
        }
    };

    const onChangeDisplayUnits = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): void => {
        if (e.target.value === '' || ALPHA_NUMBERIC.test(e.target.value)) {
            onChangeValues(e.target.value, 'displayUnit');
        }
    };

    const onChangeEquation = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): void => {
        if (e.target.value === '' || ALPHA_NUMBERIC.test(e.target.value)) {
            onChangeValues(e.target.value, 'equation');
        }
    };

    const handleClose = (): void => {
        onClose();
    };

    const handleAddProfile = (): void => {
        const findItem = derivedChannels.find(
            (channel: DerivedChannelData) => derivedChannelData.name === channel.name
        );
        if (!findItem) {
            const channelData = JSON.parse(JSON.stringify(derivedChannelData));
            props.derivedChannelData(channelData);
            props.onClose();
            reset();
        } else addToast('Device Name Already Exists');
    };

    const handleTypeChange = (value: string, type: string): void => {
        const channelDataObj: any = { ...derivedChannelData };
        channelDataObj[type] = value;
        setDerivedChannelData(channelDataObj);
    };

    return (
        <StyledDialog
            style={{ padding: 16 }}
            onClose={(): void => {
                handleClose();
            }}
            aria-labelledby="simple-dialog-title"
            open={open}
        >
            <Container className={rootClass.Container}>
                <Box>
                    <div style={{ padding: 8 }}>
                        <Typography variant="subtitle1" display="inline" style={{ lineHeight: 1.5, fontWeight: 600 }}>
                            Add Derived Channel
                        </Typography>

                        <Typography
                            variant="body2"
                            display="block"
                            style={{ marginTop: 5, lineHeight: 1.25, fontWeight: 400 }}
                        >
                            Fields marked with asterisk(*) are required field while adding a derived channel.
                        </Typography>
                    </div>
                </Box>{' '}
                <Box>
                    <Box className={`${classes.padding8}`} display="flex" justifyContent="space-between">
                        <Box style={{ width: '50%', paddingLeft: 0 }} className={`${classes.padding8}`}>
                            <TextField
                                id="channelName"
                                style={{ width: '100%' }}
                                variant="filled"
                                label="Channel Name"
                                required
                                data-testid="channelName"
                                value={derivedChannelData.name}
                                inputProps={{ 'data-testid': 'channelName', maxLength: 18 }}
                                onChange={(e: any): void => {
                                    onChangeChannelName(e);
                                }}
                            />
                        </Box>

                        <Box style={{ width: '50%', paddingRight: 0 }} className={`${classes.padding8}`}>
                            <FormControl variant="filled" required className={classes.fullWidth}>
                                <InputLabel id="channelType" data-testid="channel-type">
                                    Channel Type
                                </InputLabel>
                                <Select
                                    label="channel type "
                                    value={derivedChannelData.dataType}
                                    required
                                    inputProps={{ 'data-testid': 'type-dd' }}
                                    onChange={(e): void => {
                                        handleTypeChange(e.target.value, 'dataType');
                                    }}
                                >
                                    {channelTypeItems.map((data: { key: string; value: string }, index: number) => (
                                        <MenuItem key={index} value={data.key} data-testid={`channelType${index}`}>
                                            {data.value}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    </Box>

                    <Box className={`${classes.padding8}`} display="flex" justifyContent="space-between">
                        <Box style={{ width: '50%', paddingLeft: 0 }} className={`${classes.padding8}`}>
                            <TextField
                                id="unit"
                                style={{ width: '100%' }}
                                variant="filled"
                                label="Unit"
                                required
                                data-testid="unit"
                                value={derivedChannelData.unit}
                                inputProps={{ 'data-testid': 'unit', maxLength: 15 }}
                                onChange={(e: any): void => {
                                    onChangeUnit(e);
                                }}
                            />
                        </Box>

                        <Box style={{ width: '50%', paddingRight: 0 }} className={`${classes.padding8}`}>
                            <TextField
                                id="comPort"
                                style={{ width: '100%' }}
                                variant="filled"
                                label="Display Units"
                                required
                                data-testid="displayUnits"
                                value={derivedChannelData.displayUnit}
                                inputProps={{ 'data-testid': 'displayUnits', maxLength: 5 }}
                                onChange={(e: any): void => {
                                    onChangeDisplayUnits(e);
                                }}
                            />
                        </Box>
                    </Box>
                    <Box className={classes.paddingTextbox}>
                        <TextField
                            id="equation"
                            style={{ width: '100%', height: '50' }}
                            variant="filled"
                            label="Equation"
                            multiline
                            maxRows={3}
                            data-testid="equation"
                            value={derivedChannelData.equation}
                            inputProps={{ 'data-testid': 'equation', maxLength: 1000 }}
                            onChange={(e: any): void => {
                                onChangeEquation(e);
                            }}
                        />
                    </Box>
                    <Box className={`${classes.paddingTextbox}`} display="flex" justifyContent="space-between">
                        <Box style={{ width: '50%', paddingLeft: 0, marginBottom: 5 }}>
                            <FormControl variant="filled" required fullWidth>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={derivedChannelData.sync}
                                            style={{ margin: '6px 4px 4px 10px', padding: '0px' }}
                                            onChange={(e): void => {
                                                handleCheckChange(e.target.checked, 'sync');
                                            }}
                                        />
                                    }
                                    label={<Typography>Part of Sync Channel</Typography>}
                                />
                            </FormControl>
                        </Box>
                    </Box>
                </Box>
                <Box>
                    <Button
                        style={{ margin: 8 }}
                        variant="outlined"
                        color="primary"
                        data-testid="close-model-button"
                        onClick={handleClose}
                    >
                        Cancel
                    </Button>
                    <Button
                        style={{ float: 'right', margin: 8 }}
                        variant="contained"
                        color="primary"
                        disabled={isDisable()}
                        data-testid="export-button"
                        onClick={handleAddProfile}
                    >
                        Add Derived Channel
                    </Button>
                </Box>
            </Container>
        </StyledDialog>
    );
};
